/**
 * MultiSimulator — Broadcast one message to multiple agents simultaneously.
 * Supabase-native: each agent column gets its own independent synthetic contact.
 * No Telerivet — agents are run directly via agentfactory.
 */

import { useState, useRef, useCallback, useEffect } from "react"
import Markdown from "react-markdown"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Plus, Send, Loader2, Users, Sparkles, Mic, X, ArrowUp, LoaderCircle } from "lucide-react"
import { SimPanel, SimPanelHandle } from "./SimPanel"
import { generateFakeName } from "../utils"
import { ulid } from "ulid"
import { useVoiceRecording } from "@/hooks/use-voice-recording"

interface AgentOption {
  id: number | string
  title: string
}

interface AgentColumn {
  id: string
  agentId: string
  agentTitle: string
  label: string
}

interface MultiSimulatorProps {
  teamId: string
  agents: AgentOption[]
  loadingAgents: boolean
  preselectedAgentId?: string
}

function InlineMarkdownTitle({ value }: { value: string }) {
  return (
    <Markdown
      components={{
        p: ({ children }) => <>{children}</>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ children }) => <code className="bg-muted px-0.5 rounded text-[10px]">{children}</code>,
      }}
      allowedElements={["p", "strong", "em", "code"]}
      unwrapDisallowed
    >
      {value}
    </Markdown>
  )
}

export function MultiSimulator({ teamId, agents, loadingAgents, preselectedAgentId }: MultiSimulatorProps) {
  const [columns, setColumns] = useState<AgentColumn[]>([])
  const [selectedAgentId, setSelectedAgentId] = useState<string>("")
  const [broadcastMessage, setBroadcastMessage] = useState("")
  const [isBroadcasting, setIsBroadcasting] = useState(false)
  const [sendingPanels, setSendingPanels] = useState<Set<string>>(new Set())

  const panelRefs = useRef<Map<string, SimPanelHandle>>(new Map())

  // Auto-add preselected agent as first column
  useEffect(() => {
    if (!preselectedAgentId || loadingAgents || agents.length === 0) return
    setColumns(prev => {
      if (prev.some(c => c.agentId === preselectedAgentId)) return prev
      const agent = agents.find(a => String(a.id) === preselectedAgentId)
      if (!agent) return prev
      return [{
        id: ulid(),
        agentId: String(agent.id),
        agentTitle: agent.title,
        label: generateFakeName(),
      }, ...prev]
    })
  }, [preselectedAgentId, agents, loadingAgents])

  const addedIds = new Set(columns.map(c => c.agentId))
  const availableAgents = agents.filter(a => !addedIds.has(String(a.id)))

  const handleAddColumn = () => {
    if (!selectedAgentId) return
    const agent = agents.find(a => String(a.id) === selectedAgentId)
    if (!agent) return
    setColumns(prev => [...prev, {
      id: ulid(),
      agentId: String(agent.id),
      agentTitle: agent.title,
      label: generateFakeName(),
    }])
    setSelectedAgentId("")
  }

  const handleRemoveColumn = (colId: string) => {
    setColumns(prev => prev.filter(c => c.id !== colId))
    panelRefs.current.delete(colId)
    setSendingPanels(prev => { const next = new Set(prev); next.delete(colId); return next })
  }

  const sendBroadcastMessage = useCallback(async (rawMessage: string) => {
    const msg = rawMessage.trim()
    if (!msg || columns.length === 0) return
    setIsBroadcasting(true)
    try {
      await Promise.allSettled(
        columns.map(col => {
          const panelRef = panelRefs.current.get(col.id)
          return panelRef?.sendMessage(msg)
        })
      )
    } finally {
      setIsBroadcasting(false)
    }
  }, [columns])

  const handleBroadcast = useCallback(async () => {
    if (!broadcastMessage.trim() || columns.length === 0) return
    const msg = broadcastMessage
    setBroadcastMessage("")
    await sendBroadcastMessage(msg)
  }, [broadcastMessage, columns, sendBroadcastMessage])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleBroadcast()
    }
  }

  const anyPanelSending = sendingPanels.size > 0

  const {
    isRecording,
    recordingTime,
    isTranscribing,
    formatRecordingTime,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecording({
    teamId: teamId || undefined,
    onTranscription: sendBroadcastMessage,
    disabled: columns.length === 0 || isBroadcasting || anyPanelSending,
  })

  const handlePanelSendingChange = useCallback((colId: string, sending: boolean) => {
    setSendingPanels(prev => {
      const next = new Set(prev)
      if (sending) next.add(colId)
      else next.delete(colId)
      return next
    })
  }, [])

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top bar */}
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b">
        <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">
          {columns.length <= 1 ? "Add agent:" : "Add another:"}
        </span>

        <Select
          value={selectedAgentId}
          onValueChange={setSelectedAgentId}
          disabled={loadingAgents || availableAgents.length === 0}
        >
          <SelectTrigger className="h-7 w-[200px] text-xs">
            <SelectValue placeholder={
              loadingAgents ? "Loading agents…"
                : availableAgents.length === 0 ? "All agents added"
                  : "Select agent to add…"
            } />
          </SelectTrigger>
          <SelectContent>
            {availableAgents.map(agent => (
              <SelectItem key={agent.id} value={String(agent.id)} className="text-xs">
                <InlineMarkdownTitle value={agent.title} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5" onClick={handleAddColumn} disabled={!selectedAgentId}>
          <Plus className="h-3 w-3" />
          Add
        </Button>

        <div className="flex-1" />

        {columns.length > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-default">
                  <Badge variant="secondary" className="text-[10px] gap-1">
                    <Sparkles className="h-2.5 w-2.5" />
                    {columns.length} agent{columns.length !== 1 ? "s" : ""}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                Broadcast sends the same message to all agents simultaneously
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Panel grid */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {columns.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
            <Users className="h-10 w-10 opacity-20" />
            <p className="text-sm">Select an agent above to start chatting</p>
            <p className="text-xs opacity-60">Add more agents to compare responses side by side</p>
          </div>
        ) : (
          <div
            className="grid h-full gap-3 p-3"
            style={{ gridTemplateColumns: `repeat(${Math.min(columns.length, 4)}, minmax(0, 1fr))` }}
          >
            {columns.map(col => (
              <SimPanel
                key={col.id}
                ref={(handle) => {
                  if (handle) panelRefs.current.set(col.id, handle)
                  else panelRefs.current.delete(col.id)
                }}
                agentId={col.agentId}
                agentTitle={col.agentTitle}
                label={col.label}
                teamId={teamId}
                onRemove={() => handleRemoveColumn(col.id)}
                onSendingChange={(sending) => handlePanelSendingChange(col.id, sending)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Broadcast input */}
      <div className="shrink-0 px-3 py-2 border-t bg-muted/20">
        <div className="flex gap-2 items-end">
          <Textarea
            placeholder={
              columns.length === 0 ? "Select an agent above to start…"
                : columns.length === 1 ? "Type a message… (Enter to send)"
                  : `Broadcast to all ${columns.length} agents… (Enter to send)`
            }
            value={broadcastMessage}
            onChange={(e) => setBroadcastMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={columns.length === 0 || isBroadcasting || isTranscribing}
            className="flex-1 text-sm resize-none min-h-[38px] max-h-[120px]"
            rows={1}
          />

          {isRecording && (
            <>
              <Button
                type="button"
                onClick={cancelRecording}
                variant="ghost"
                size="icon"
                title="Cancel recording"
                className="h-9 w-9 shrink-0"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
              </Button>
              <div className="flex items-center gap-2 px-2 h-9">
                <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs text-red-600 font-medium tabular-nums">
                  {formatRecordingTime(recordingTime)}
                </span>
              </div>
            </>
          )}

          <Button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            variant={isRecording ? "destructive" : "outline"}
            size="icon"
            disabled={columns.length === 0 || isBroadcasting || isTranscribing || anyPanelSending}
            title={isRecording ? "Send voice note" : "Record voice note"}
            className="h-9 w-9 shrink-0"
          >
            {isRecording
              ? <ArrowUp className="h-4 w-4" />
              : <Mic className="h-4 w-4" />}
          </Button>

          <Button
            onClick={handleBroadcast}
            disabled={columns.length === 0 || !broadcastMessage.trim() || isBroadcasting || isTranscribing || anyPanelSending}
            className="h-9 w-9 shrink-0"
            size="icon"
          >
            {isBroadcasting || anyPanelSending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Send className="h-4 w-4" />
            }
          </Button>
        </div>
        {isTranscribing && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-2">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            <span>Transcribing…</span>
          </div>
        )}
        {columns.length > 1 && (
          <p className="text-[10px] text-muted-foreground mt-1">
            Message sent to all {columns.length} agents simultaneously · Each agent has its own independent synthetic conversation
          </p>
        )}
      </div>
    </div>
  )
}
